import { ImagePlus, Trash2 } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { toISODate, toTime } from '@core/dates';
import { currencyDigits, minorToInput, parseAmount } from '@core/money';

import { fetchBlobUrl } from '@/lib/api';
import { useApp, useMoney } from '@/lib/hooks';
import { liveCategories } from '@/lib/merge';
import { removeReceipt, saveExpense, setExpenseDeleted, uploadReceipt } from '@/lib/store';
import { PAYMENT_METHODS } from '@/lib/types';

import { Button, Chip, Drawer, useToast } from './ui';

type EditorApi = { open: (id?: string) => void };
const EditorCtx = createContext<EditorApi>({ open: () => {} });

/** Mount once in the layout: wrap the app in <EditorProvider value={api}> and render `element`. */
export function useExpenseEditor() {
  const [target, setTarget] = useState<{ id?: string; key: number } | null>(null);
  const open = useCallback((id?: string) => setTarget({ id, key: Date.now() }), []);
  const api = useMemo<EditorApi>(() => ({ open }), [open]);
  return {
    api,
    open,
    element: target && <ExpenseEditor key={target.key} id={target.id} onClose={() => setTarget(null)} />,
  };
}

export const useEditor = () => useContext(EditorCtx);
export const EditorProvider = EditorCtx.Provider;

function ExpenseEditor({ id, onClose }: { id?: string; onClose: () => void }) {
  const { data } = useApp();
  const { currency, format } = useMoney();
  const toast = useToast();
  const existing = id ? data.expenses.get(id) : undefined;
  const cur = existing?.currency ?? currency;
  const digits = currencyDigits(cur);
  const categories = liveCategories(data).filter((c) => !c.isArchived || c.id === existing?.categoryId);
  const now = new Date();

  const [amount, setAmount] = useState(existing ? minorToInput(existing.amountMinor, digits) : '');
  const [title, setTitle] = useState(existing?.title ?? '');
  const [categoryId, setCategoryId] = useState(existing?.categoryId ?? categories[0]?.id ?? '');
  const [date, setDate] = useState(existing?.expenseDate ?? toISODate(now));
  const [time, setTime] = useState(existing?.expenseTime ?? toTime(now));
  const [merchant, setMerchant] = useState(existing?.merchant ?? '');
  const [payment, setPayment] = useState<string | null>(existing?.paymentMethodId ?? null);
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const amountMinor = parseAmount(amount, digits);
  const receipt = id ? data.receipts.get(id) : undefined;
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);

  useEffect(() => {
    if (!receipt || !id) return;
    let url: string | null = null;
    fetchBlobUrl(`/receipts/${encodeURIComponent(id)}`)
      .then((u) => setReceiptUrl((url = u)))
      .catch(() => setReceiptUrl(null));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [id, receipt?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (amountMinor === null || !categoryId) return;
    setBusy(true);
    try {
      const saved = saveExpense(
        {
          amountMinor,
          currency: cur,
          categoryId,
          title: title.trim(),
          merchant: merchant.trim() || null,
          paymentMethodId: payment,
          notes: notes.trim() || null,
          expenseDate: date,
          expenseTime: time || null,
        },
        id,
      );
      onClose();
      toast({
        message: existing ? 'Changes saved' : `Saved ${format(amountMinor)}`,
        action: existing ? undefined : { label: 'Undo', run: () => setExpenseDeleted(saved.id, true) },
      });
      if (file) {
        uploadReceipt(saved.id, file).catch((e: Error) => toast({ message: e.message || "Couldn't upload the receipt" }));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      title={existing ? 'Edit expense' : 'Add expense'}
      onClose={onClose}
      footer={
        <>
          {existing && (
            <Button
              variant="secondary"
              icon={Trash2}
              onClick={() => {
                setExpenseDeleted(existing.id, true);
                onClose();
                toast({ message: 'Expense deleted', action: { label: 'Undo', run: () => setExpenseDeleted(existing.id, false) } });
              }}>
              Delete
            </Button>
          )}
          <Button block size="lg" disabled={amountMinor === null || !categoryId} loading={busy} onClick={save}>
            {amountMinor !== null ? `Save ${format(amountMinor)}` : 'Save expense'}
          </Button>
        </>
      }>
      <label className="field">
        <span>Amount</span>
        <input
          className="amount-input num"
          inputMode="decimal"
          placeholder="0"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ''))}
          autoFocus
          aria-invalid={amount !== '' && amountMinor === null}
        />
      </label>
      <label className="field">
        <span>What for?</span>
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} placeholder="Lunch" />
      </label>
      <div className="field">
        <span>Category</span>
        <div className="chips">
          {categories.map((c) => (
            <Chip key={c.id} on={c.id === categoryId} onClick={() => setCategoryId(c.id)}>
              {c.name}
            </Chip>
          ))}
        </div>
      </div>
      <div className="row" style={{ gap: 12 }}>
        <label className="field" style={{ flex: 1 }}>
          <span>Date</span>
          <input className="input" type="date" value={date} max={toISODate(now)} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="field" style={{ width: 130 }}>
          <span>Time</span>
          <input className="input" type="time" value={time ?? ''} onChange={(e) => setTime(e.target.value)} />
        </label>
      </div>
      <label className="field">
        <span>Merchant</span>
        <input className="input" value={merchant} onChange={(e) => setMerchant(e.target.value)} maxLength={80} placeholder="Optional" />
      </label>
      <div className="field">
        <span>Payment method</span>
        <div className="chips">
          {PAYMENT_METHODS.map((p) => (
            <Chip key={p.id} on={payment === p.id} onClick={() => setPayment(payment === p.id ? null : p.id)}>
              {p.name}
            </Chip>
          ))}
        </div>
      </div>
      <label className="field">
        <span>Notes</span>
        <textarea className="textarea" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} />
      </label>
      <div className="field">
        <span>Receipt</span>
        {preview || receiptUrl ? (
          <img className="receipt-thumb" src={preview ?? receiptUrl!} alt="Receipt" />
        ) : null}
        <div className="row">
          <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
            <ImagePlus size={18} />
            {preview || receipt ? 'Replace image' : 'Attach image'}
            <input type="file" accept="image/*" hidden onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </label>
          {receipt && !file && id && (
            <Button
              variant="ghost"
              onClick={() =>
                removeReceipt(id)
                  .then(() => setReceiptUrl(null))
                  .catch(() => toast({ message: "Couldn't remove the receipt" }))
              }>
              Remove
            </Button>
          )}
        </div>
      </div>
    </Drawer>
  );
}
